/**
 * OD-38 Full HopEx admin authority, presented as grouped operations (owner product review §31–32).
 * Actions live in menus («عملیات مدیریت ▾», «عملیات احراز ▾» …) instead of dozens of equal buttons.
 * Every high-impact action still goes through ONE confirmation dialog — user, current state, new state,
 * mandatory reason, «تأیید و اعمال» — and the server still demands a recent admin step-up (the dialog
 * collects the TOTP code and retries). Overrides are recorded as ADMIN_OVERRIDE; provider evidence is
 * never changed. Semantics: approve = positive · request correction = neutral · override = caution ·
 * reject / revoke / reset / lock = danger · unlock = positive.
 */
import {
  ActionMenu,
  Button,
  Dialog,
  TextField,
  type ActionMenuSection,
  type ActionTone,
} from '@hopex/ui';
import { useState, type ReactNode } from 'react';
import { api, ApiFailure, dt, errorMessage, type AdminUserDetail } from '../api.ts';
import {
  ACCOUNT_STATUS,
  KYC1_STATUS,
  LEVEL_LABEL,
  OWNERSHIP,
  SOURCE_LABEL,
} from '../lib/labels.ts';

const REASONS = [
  'بررسی توسط پشتیبانی',
  'مدرک جایگزین',
  'اصلاح اطلاعات کاربر',
  'تصمیم مدیریت',
  'بررسی دستی',
];

export interface ActionSpec {
  key: string;
  label: string;
  title: string;
  endpoint: string;
  body: Record<string, unknown>;
  current: string;
  next: string;
  tone: ActionTone;
  description?: string;
  /** Extra inputs collected in the dialog (merged into the body). */
  form?: 'identity' | 'phone' | 'kyc2Targets';
}

const KYC2_TARGETS = [
  ['FATHER_NAME', 'نام پدر'],
  ['SELFIE', 'تصویر سلفی با کارت ملی و دست‌نوشته'],
  ['RULES', 'پذیرش قوانین'],
] as const;

const CONFIRM_VARIANT: Record<ActionTone, 'primary' | 'positive' | 'caution' | 'danger'> = {
  neutral: 'primary',
  positive: 'positive',
  caution: 'caution',
  danger: 'danger',
};

export function ConfirmAction({
  spec,
  userLabel,
  onClose,
  onDone,
}: {
  spec: ActionSpec;
  userLabel: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [preset, setPreset] = useState(REASONS[0] as string);
  const [note, setNote] = useState('');
  const [stepUp, setStepUp] = useState(false);
  const [code, setCode] = useState('');
  const [extra, setExtra] = useState<Record<string, string>>({});
  const [reverify, setReverify] = useState(true);
  const [targets, setTargets] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reason = note.trim() ? `${preset} — ${note.trim()}` : preset;

  function body(): Record<string, unknown> {
    const b: Record<string, unknown> = { ...spec.body, reason };
    if (spec.form === 'phone')
      Object.assign(b, { newPhone: extra.newPhone ?? '', requireReverification: reverify });
    if (spec.form === 'kyc2Targets') b.correctionTargets = targets;
    if (spec.form === 'identity') {
      for (const k of ['firstName', 'lastName', 'nationalId', 'fatherName'] as const)
        if (extra[k]?.trim()) b[k] = extra[k]?.trim();
      if (extra.jy && extra.jm && extra.jd)
        b.birthDate = { jy: Number(extra.jy), jm: Number(extra.jm), jd: Number(extra.jd) };
    }
    return b;
  }

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      if (stepUp) {
        await api.post('/auth/step-up', { code });
        setStepUp(false);
        setCode('');
      }
      await api.post(spec.endpoint, body());
      onDone();
    } catch (e) {
      if (e instanceof ApiFailure && e.code === 'STEP_UP_REQUIRED') setStepUp(true);
      else setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  const input = (k: string, label: string, ltr = false) => (
    <TextField
      label={label}
      value={extra[k] ?? ''}
      onChange={(v) => setExtra((x) => ({ ...x, [k]: v }))}
      ltr={ltr}
    />
  );

  return (
    <Dialog
      isOpen
      onOpenChange={(o) => !o && onClose()}
      title={spec.title}
      isDismissDisabled={busy}
      footer={
        <>
          <Button variant="ghost" onPress={onClose} isDisabled={busy}>
            انصراف
          </Button>
          <Button
            variant={CONFIRM_VARIANT[spec.tone]}
            loading={busy}
            isDisabled={spec.form === 'kyc2Targets' && targets.length === 0}
            onPress={() => void submit()}
          >
            تأیید و اعمال
          </Button>
        </>
      }
    >
      <div
        className="flex flex-col gap-3 text-fg"
        data-confirm-action={spec.key}
        data-tone={spec.tone}
      >
        <dl className="m-0 grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 rounded-md border border-line bg-surface-1 p-3 text-[13px]">
          <dt className="text-fg-muted">کاربر</dt>
          <dd className="m-0 font-semibold">{userLabel}</dd>
          <dt className="text-fg-muted">وضعیت فعلی</dt>
          <dd className="m-0">{spec.current}</dd>
          <dt className="text-fg-muted">وضعیت جدید</dt>
          <dd className="m-0 font-semibold">{spec.next}</dd>
        </dl>
        {spec.form === 'identity' && (
          <div className="grid gap-2 sm:grid-cols-2" data-identity-form>
            {input('firstName', 'نام جدید')}
            {input('lastName', 'نام خانوادگی جدید')}
            {input('fatherName', 'نام پدر')}
            {input('nationalId', 'کد ملی جدید (موارد استثنایی)', true)}
            <div className="grid grid-cols-3 gap-2 sm:col-span-2">
              {input('jy', 'سال تولد (شمسی)', true)}
              {input('jm', 'ماه', true)}
              {input('jd', 'روز', true)}
            </div>
            <p className="m-0 text-[12px] text-fg-subtle sm:col-span-2">
              فقط فیلدهای پرشده تغییر می‌کنند. نسخه‌ی تازه‌ای از هویت ساخته می‌شود و نسخه‌ی قبلی در
              سابقه می‌ماند.
            </p>
          </div>
        )}
        {spec.form === 'kyc2Targets' && (
          <fieldset className="m-0 flex flex-col gap-1.5 border-0 p-0" data-kyc2-targets>
            <legend className="mb-1 text-[13px] text-fg-muted">
              کدام بخش‌ها باید دوباره ارسال شوند؟ (حداقل یکی)
            </legend>
            {KYC2_TARGETS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-[13px]">
                <input
                  type="checkbox"
                  checked={targets.includes(k)}
                  onChange={(e) =>
                    setTargets((t) => (e.target.checked ? [...t, k] : t.filter((x) => x !== k)))
                  }
                  data-kyc2-target={k}
                />
                {label}
              </label>
            ))}
          </fieldset>
        )}
        {spec.form === 'phone' && (
          <div className="flex flex-col gap-2">
            {input('newPhone', 'شماره‌ی موبایل جدید', true)}
            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={reverify}
                onChange={(e) => setReverify(e.target.checked)}
              />
              کاربر باید احراز سطح ۱ را با شماره‌ی جدید دوباره انجام دهد
            </label>
          </div>
        )}
        <label className="flex flex-col gap-1 text-[13px]">
          <span className="text-fg-muted">دلیل (الزامی)</span>
          <select
            value={preset}
            onChange={(e) => setPreset(e.target.value)}
            className="h-11 rounded-md border border-line bg-surface-1 px-3 text-fg"
            data-reason
          >
            {REASONS.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </select>
        </label>
        <TextField label="توضیح (اختیاری)" value={note} onChange={setNote} />
        {stepUp && (
          <div className="rounded-md border border-accent-soft p-3" data-admin-step-up>
            <p className="m-0 mb-2 text-[13px]">
              برای این اقدام، کد فعلی برنامه‌ی Authenticator مدیر را وارد کنید.
            </p>
            <TextField
              label="کد ورود دومرحله‌ای مدیر"
              value={code}
              onChange={setCode}
              ltr
              inputMode="numeric"
            />
          </div>
        )}
        {error && (
          <p role="alert" className="m-0 text-[13px] text-negative">
            {error}
          </p>
        )}
      </div>
    </Dialog>
  );
}

/* ───────── action catalogue (per customer) ───────── */

export interface ActionGroups {
  kyc: ActionSpec[];
  identity: ActionSpec[];
  security: ActionSpec[];
  account: ActionSpec[];
  bank: (b: AdminUserDetail['bankInstruments'][number]) => ActionSpec[];
}

export function buildActions(d: AdminUserDetail): ActionGroups {
  const u = `/users/${d.user.id}`;
  const k1 = d.kyc?.status ?? 'NOT_STARTED';
  const k1Label = `${KYC1_STATUS[k1] ?? k1} · ${SOURCE_LABEL(d.kyc?.verificationSource)}`;
  const k2Label = `${KYC1_STATUS[d.kyc2.status] ?? d.kyc2.status} · ${SOURCE_LABEL(d.kyc2.verificationSource)}`;
  const kyc = (
    key: string,
    label: string,
    action: string,
    next: string,
    tone: ActionTone,
    description?: string,
  ): ActionSpec => ({
    key,
    label,
    title: `${label} — مدیریت احراز هویت`,
    endpoint: `${u}/kyc/actions`,
    body: { action },
    current: `سطح ۱: ${k1Label} | سطح ۲: ${k2Label}`,
    next,
    tone,
    ...(description ? { description } : {}),
  });
  const status = (st: 'ACTIVE' | 'SUSPENDED' | 'LOCKED', label: string): ActionSpec => ({
    key: `ACCOUNT_${st}`,
    label,
    title: `${label} حساب`,
    endpoint: `${u}/account/status`,
    body: { status: st },
    current: ACCOUNT_STATUS[d.user.status] ?? d.user.status,
    next: ACCOUNT_STATUS[st] ?? st,
    tone: st === 'ACTIVE' ? 'positive' : 'danger',
  });
  const totp = (
    action: 'DISABLE' | 'RESET' | 'REQUIRE_ENROLLMENT',
    label: string,
    next: string,
  ): ActionSpec => ({
    key: `TOTP_${action}`,
    label,
    title: label,
    endpoint: `${u}/security/totp`,
    body: { action },
    current: `ورود دومرحله‌ای ${d.security.totpEnabled ? 'فعال' : 'غیرفعال'}${d.security.totpRequired ? ' · الزامی' : ''} · کدهای بازیابی: ${d.security.recoveryCodesRemaining}`,
    next,
    tone: action === 'REQUIRE_ENROLLMENT' ? 'caution' : 'danger',
  });
  const activeSessions = d.sessions.filter((s) => !s.revokedAt);
  return {
    kyc: [
      kyc('KYC1_APPROVE', 'تأیید سطح ۱', 'KYC1_APPROVE', 'سطح ۱ تأییدشده (تصمیم مدیر)', 'positive'),
      kyc('KYC1_RETURN', 'درخواست اصلاح سطح ۱', 'KYC1_RETURN', 'نیازمند اصلاح', 'neutral'),
      kyc('KYC1_REJECT', 'رد سطح ۱', 'KYC1_REJECT', 'سطح ۱ ردشده', 'danger'),
      kyc(
        'KYC1_REVOKE',
        'حذف احراز سطح ۱',
        'KYC1_REVOKE',
        'سطح ۱ انجام‌نشده (سطح ۲ هم حذف می‌شود)',
        'danger',
      ),
      kyc('KYC2_APPROVE', 'تأیید سطح ۲', 'KYC2_APPROVE', 'سطح ۲ تأییدشده (تصمیم مدیر)', 'positive'),
      {
        ...kyc(
          'KYC2_RETURN',
          'درخواست اصلاح سطح ۲',
          'KYC2_RETURN',
          'سطح ۲ نیازمند اصلاح',
          'neutral',
        ),
        // An open KYC2 attempt is returned with the exact steps to redo (server requires ≥ 1).
        ...(d.kyc2Submissions[0]?.status === 'SUBMITTED' ? { form: 'kyc2Targets' as const } : {}),
      },
      kyc('KYC2_REJECT', 'رد سطح ۲', 'KYC2_REJECT', 'سطح ۲ ردشده', 'danger'),
      kyc('KYC2_REVOKE', 'حذف احراز سطح ۲', 'KYC2_REVOKE', 'سطح ۲ انجام‌نشده', 'danger'),
      kyc(
        'SET_LEVEL_1',
        'تعیین مستقیم سطح ۱',
        'SET_LEVEL_1',
        'سطح مؤثر ۱ (بدون مدرک)',
        'caution',
        'تصمیم مدیر بدون استعلام',
      ),
      kyc(
        'SET_LEVEL_2',
        'ارتقای مستقیم به سطح ۲',
        'SET_LEVEL_2',
        'سطح مؤثر ۲ (بدون مدرک)',
        'caution',
        'تصمیم مدیر بدون مدرک سطح ۲',
      ),
      kyc('SET_LEVEL_1_DOWN', 'بازگردانی به سطح ۱', 'KYC2_REVOKE', 'سطح مؤثر ۱', 'danger'),
      kyc('SET_LEVEL_0', 'بازنشانی کامل احراز', 'SET_LEVEL_0', 'بدون احراز (سطح ۰)', 'danger'),
    ],
    identity: [
      {
        key: 'IDENTITY',
        label: 'اصلاح هویت',
        title: 'اصلاح هویت',
        endpoint: `${u}/identity/correct`,
        body: {},
        current: d.kyc?.identity
          ? `${d.kyc.identity.legalName} · ${d.kyc.identity.nationalIdMasked} · ${d.kyc.identity.birthYear}`
          : 'بدون اطلاعات هویتی',
        next: 'مقادیر جدید (پایین)',
        tone: 'caution',
        description: 'نام، تاریخ تولد، نام پدر؛ کد ملی در موارد استثنایی',
        form: 'identity',
      },
    ],
    security: [
      totp(
        'REQUIRE_ENROLLMENT',
        'الزام راه‌اندازی ورود دومرحله‌ای',
        'کاربر در ورود بعدی باید فعال کند',
      ),
      totp('RESET', 'بازنشانی ورود دومرحله‌ای', 'باطل‌سازی و الزام فعال‌سازی دوباره'),
      totp(
        'DISABLE',
        'غیرفعال‌کردن ورود دومرحله‌ای',
        'غیرفعال؛ کلید و کدهای بازیابی باطل؛ خروج از همه‌ی دستگاه‌ها',
      ),
      {
        key: 'REVOKE_ALL',
        label: 'خروج اجباری از همه‌ی دستگاه‌ها',
        title: 'خروج اجباری از همه‌ی دستگاه‌ها',
        endpoint: `${u}/sessions/revoke`,
        body: {},
        current: `${activeSessions.length.toLocaleString('fa-IR')} نشست فعال`,
        next: 'همه‌ی نشست‌ها بسته می‌شوند',
        tone: 'danger',
      },
      ...activeSessions.map((s): ActionSpec => ({
        key: `REVOKE_${s.id}`,
        label: `خروج ${s.device ?? 'دستگاه'}`,
        title: 'خروج اجباری یک دستگاه',
        endpoint: `${u}/sessions/revoke`,
        body: { sessionId: s.id },
        current: `${s.device ?? 'دستگاه'} · فعال · ${dt(s.lastSeenAt)}`,
        next: 'نشست بسته می‌شود',
        tone: 'danger',
      })),
    ],
    account: [
      ...(d.user.status !== 'ACTIVE' ? [status('ACTIVE', 'فعال‌سازی / رفع قفل')] : []),
      ...(d.user.status !== 'LOCKED' ? [status('LOCKED', 'قفل')] : []),
      ...(d.user.status !== 'SUSPENDED' ? [status('SUSPENDED', 'تعلیق')] : []),
      {
        key: 'PHONE',
        label: 'تغییر شماره‌ی موبایل',
        title: 'تغییر اداری شماره‌ی موبایل',
        endpoint: `${u}/phone`,
        body: {},
        current: `${d.user.phoneMasked ?? '—'} (${d.security.phoneSource === 'ADMIN' ? 'ثبت‌شده توسط مدیر' : 'تأییدشده با کد پیامکی'})`,
        next: 'شماره‌ی جدید (منبع: مدیر؛ تطابق مالکیت قبلی باطل می‌شود)',
        tone: 'caution',
        form: 'phone',
      },
    ],
    bank: (b) =>
      (['VERIFY', 'REJECT', b.active ? 'DEACTIVATE' : 'REACTIVATE'] as const).map((a) => ({
        key: `BANK_${a}_${b.id}`,
        label:
          a === 'VERIFY'
            ? 'تأیید دستی مالکیت'
            : a === 'REJECT'
              ? 'رد مالکیت'
              : a === 'DEACTIVATE'
                ? 'غیرفعال‌سازی'
                : 'فعال‌سازی دوباره',
        title: 'مدیریت کارت / شبا',
        endpoint: `/bank-instruments/${b.id}/actions`,
        body: { action: a },
        current: `${b.masked} · ${OWNERSHIP[b.ownershipStatus] ?? b.ownershipStatus} · ${b.active ? 'فعال' : 'غیرفعال'}`,
        next:
          a === 'VERIFY'
            ? 'مالکیت تأییدشده (تصمیم مدیر)'
            : a === 'REJECT'
              ? 'مالکیت ردشده'
              : a === 'DEACTIVATE'
                ? 'غیرفعال'
                : 'فعال',
        tone: a === 'VERIFY' ? 'caution' : a === 'REACTIVATE' ? 'positive' : 'danger',
      })),
  };
}

const toItems = (specs: ActionSpec[]) =>
  specs.map((s) => ({
    id: s.key,
    label: s.label,
    tone: s.tone,
    ...(s.description ? { description: s.description } : {}),
  }));

/** One menu → one confirmation dialog. `groups` chooses which sections appear. */
export function AdminActionMenu({
  d,
  onChanged,
  label,
  sections,
  variant = 'primary',
  size = 'md',
  ...rest
}: {
  d: AdminUserDetail;
  onChanged: () => void;
  label: string;
  sections: { title: string; specs: ActionSpec[] }[];
  variant?: 'primary' | 'secondary';
  size?: 'sm' | 'md';
} & Record<`data-${string}`, unknown>) {
  const [open, setOpen] = useState<ActionSpec | null>(null);
  const all = sections.flatMap((s) => s.specs);
  const menu: ActionMenuSection[] = sections
    .filter((s) => s.specs.length > 0)
    .map((s) => ({ title: s.title, items: toItems(s.specs) }));
  const userLabel = `${d.user.displayName ?? 'بدون نام'} (${d.user.phoneMasked ?? '—'})`;
  return (
    <>
      <ActionMenu
        {...rest}
        label={label}
        sections={menu}
        variant={variant}
        size={size}
        onAction={(id) => setOpen(all.find((s) => s.key === id) ?? null)}
      />
      {open && (
        <ConfirmAction
          spec={open}
          userLabel={userLabel}
          onClose={() => setOpen(null)}
          onDone={() => {
            setOpen(null);
            onChanged();
          }}
        />
      )}
    </>
  );
}

export const levelSummary = (d: AdminUserDetail): string =>
  `${LEVEL_LABEL[d.effectiveLevel] ?? d.effectiveLevel}${
    d.effectiveLevel === 'KYC2'
      ? ` · ${SOURCE_LABEL(d.kyc2.verificationSource)}`
      : d.effectiveLevel === 'KYC1'
        ? ` · ${SOURCE_LABEL(d.kyc?.verificationSource)}`
        : ''
  }`;

export function Note({ children }: { children: ReactNode }) {
  return (
    <p
      className="m-0 rounded-md border border-line bg-surface-2 px-3.5 py-2.5 text-[12.5px] leading-6 text-fg-muted"
      data-override-note
    >
      {children}
    </p>
  );
}
